import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs, doc, getDoc, setDoc, addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import JobListTable from './JobListTable';
import JobFormModal from './JobFormModal';
import JobDetailsPanel from './JobDetailsPanel';
import { deleteJob } from '../../services/firebase';
import { getAuth } from 'firebase/auth';

export default function JobManagementView() {
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isJobFormOpen, setIsJobFormOpen] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showJobDetails, setShowJobDetails] = useState(false);

  // Fetch all jobs
  useEffect(() => {
    const fetchJobs = async () => {
      setLoadingJobs(true);
      try {
        const db = getFirestore();
        const jobsSnapshot = await getDocs(collection(db, 'jobs'));
        
        const jobsData = jobsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setJobs(jobsData);
      } catch (error) {
        console.error("Error fetching jobs:", error);
        toast.error(`Failed to load jobs: ${error.message}`);
      } finally {
        setLoadingJobs(false);
      }
    };

    fetchJobs();
  }, []);

  // Handle selecting a job to view details
  const handleJobSelect = async (jobId) => {
    setSelectedJobId(jobId);
    setIsFormLoading(true);
    
    try {
      const db = getFirestore();
      const jobDoc = await getDoc(doc(db, 'jobs', jobId));
      
      if (jobDoc.exists()) {
        setSelectedJob({ id: jobId, ...jobDoc.data() });
        setShowJobDetails(true);
      } else {
        toast.error("Job not found");
        setSelectedJob(null);
      }
    } catch (error) {
      console.error("Error fetching job details:", error);
      toast.error(`Failed to load job details: ${error.message}`);
      setSelectedJob(null);
    } finally {
      setIsFormLoading(false);
    }
  };
  
  // Handle editing a job from details panel
  const handleEditFromDetails = (job) => {
    setSelectedJob(job);
    setSelectedJobId(job.id);
    setIsJobFormOpen(true);
  };
  
  // Handle closing job details panel
  const handleCloseJobDetails = () => {
    setShowJobDetails(false);
    setSelectedJobId(null);
    setSelectedJob(null);
  };

  // Handle opening the form to add a new job
  const handleAddJobClick = () => {
    setSelectedJobId(null);
    setSelectedJob(null);
    setIsJobFormOpen(true);
  };

  // Handle closing the job form
  const handleCloseJobForm = () => {
    setIsJobFormOpen(false);
    // If we're editing from details panel, go back to details
    if (showJobDetails) {
      // Keep the selected job for the details panel
    } else {
      setSelectedJobId(null);
      setSelectedJob(null);
    }
  };

  // Handle initiating job deletion
  const handleDeleteClick = (job) => {
    setJobToDelete(job);
    setShowDeleteConfirm(true);
  };

  // Handle confirming job deletion
  const confirmDeleteJob = async () => {
    if (!jobToDelete) return;
    
    setIsDeleting(true);
    try {
      // Get the current admin's user ID from auth
      const auth = getAuth();
      const adminId = auth.currentUser ? auth.currentUser.uid : null;
      const adminEmail = auth.currentUser ? auth.currentUser.email : 'Unknown admin';
      
      const result = await deleteJob(jobToDelete.id, adminId, adminEmail);
      
      if (result.success) {
        toast.success('Job deleted successfully and archived for affected users');
        // Remove the job from the local state
        setJobs(jobs.filter(job => job.id !== jobToDelete.id));
      } else {
        toast.error(`Failed to delete job: ${result.error}`);
      }
    } catch (error) {
      console.error("Error deleting job:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setJobToDelete(null);
    }
  };

  // Handle saving a job (either add or update)
  const handleSaveJob = async (jobData) => {
    setIsFormLoading(true);
    
    try {
      const db = getFirestore();
      
      // Remove any undefined fields to prevent Firestore errors
      const cleanJobData = Object.fromEntries(
        Object.entries(jobData).filter(([_, value]) => value !== undefined)
      );
      
      // Prepare job data with timestamps
      const { id, ...jobDataWithoutId } = jobData;
      
      const jobDataWithTimestamps = {
        ...cleanJobData,
        updatedAt: serverTimestamp()
      };
      
      // Add createdAt for new jobs
      if (!cleanJobData.id) {
        jobDataWithTimestamps.createdAt = serverTimestamp();
      }
      
      // Get the current admin's user ID from auth
      const auth = getAuth();
      const adminId = auth.currentUser ? auth.currentUser.uid : null;
      const adminEmail = auth.currentUser ? auth.currentUser.email : 'Unknown admin';
      
      // Add admin info
      jobDataWithTimestamps.updatedBy = adminEmail;
      if (!cleanJobData.id) {
        jobDataWithTimestamps.createdBy = adminEmail;
      }
      
      let savedJob;
      
      if (cleanJobData.id) {
        // Update existing job
        const jobRef = doc(db, 'jobs', cleanJobData.id);
        
        // Remove id before updating (Firestore doesn't need/want the id in the document data)
        const { id, ...dataWithoutId } = jobDataWithTimestamps;
        await updateDoc(jobRef, dataWithoutId);
        
        savedJob = { id: cleanJobData.id, ...dataWithoutId };
        toast.success('Job updated successfully');
      } else {
        // Create new job
        const jobRef = collection(db, 'jobs');
        const docRef = await addDoc(jobRef, jobDataWithTimestamps);
        savedJob = { id: docRef.id, ...jobDataWithTimestamps };
        toast.success('Job added successfully');
      }
      
      // Update local state
      setJobs(prevJobs => {
        if (cleanJobData.id) {
          // Replace the updated job
          return prevJobs.map(job => job.id === cleanJobData.id ? savedJob : job);
        } else {
          // Add the new job
          return [...prevJobs, savedJob];
        }
      });
      
      // Close the form
      setIsJobFormOpen(false);
      
      // If we were editing from details panel, update the selected job
      if (showJobDetails && cleanJobData.id) {
        setSelectedJob(savedJob);
      } else {
        setSelectedJobId(null);
        setSelectedJob(null);
      }
    } catch (error) {
      console.error("Error saving job:", error);
      toast.error(`Failed to save job: ${error.message}`);
    } finally {
      setIsFormLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Job Management</h2>
      
      {/* Job List and Add Job button */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Job Listings</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              All jobs available on the platform
            </p>
          </div>
          <button
            type="button"
            onClick={handleAddJobClick}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Add Job
          </button>
        </div>
        
        {/* Job List Table */}
        <JobListTable 
          jobs={jobs}
          isLoading={loadingJobs}
          onJobSelect={handleJobSelect}
          onAddJobClick={handleAddJobClick}
          onDeleteClick={handleDeleteClick}
        />
      </div>
      
      {/* Job Form Modal */}
      <JobFormModal
        job={selectedJob}
        isOpen={isJobFormOpen}
        onClose={handleCloseJobForm}
        onSave={handleSaveJob}
        isLoading={isFormLoading}
      />
      
      {/* Job Details Panel */}
      <JobDetailsPanel
        job={selectedJob}
        isLoading={isFormLoading}
        onClose={handleCloseJobDetails}
        onEdit={handleEditFromDetails}
        onDelete={handleDeleteClick}
      />
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden w-full max-w-md p-6">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Job</h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to delete this job? This job will be archived in users' profiles and not shown to them anymore, but the data will be preserved.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={confirmDeleteJob}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setJobToDelete(null);
                }}
                disabled={isDeleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 